import { useState } from "react";
import { z } from "zod";
import { fcfa, useApiEnveloppe } from "../../api/hooks";
import { EtatChargement, EtatErreur, ListeAvis, Refus } from "../../composants/Avis";
import { Tableau, Tuiles } from "../../composants/Tableau";
import type { EcranProps } from "../types";

// ===========================================================================
// GRAND LIVRE
// ===========================================================================

const CompteSchema = z.object({
  numero: z.string(),
  intitule: z.string(),
  aNouveau: z.number(),
  totalDebit: z.number(),
  totalCredit: z.number(),
  soldeFinal: z.number(),
  sensSoldeFinal: z.string(),
  mouvements: z.array(
    z.object({
      date: z.string(),
      journal: z.string(),
      reference: z.string(),
      libelle: z.string(),
      debit: z.number(),
      credit: z.number(),
      soldeProgressif: z.number(),
      sensSolde: z.string(),
      soldeAbsolu: z.number(),
    }),
  ),
});

const GrandLivreSchema = z.object({
  comptes: z.array(CompteSchema),
  branches: z.array(
    z.object({
      prefixe: z.string(),
      intitule: z.string(),
      nbComptes: z.number(),
      soldeNet: z.number(),
    }),
  ),
});

export function GrandLivre({ dossierId, annee }: EcranProps) {
  const [prefixe, setPrefixe] = useState("");
  const [ouvert, setOuvert] = useState<string | null>(null);

  const q = useApiEnveloppe(
    ["grand-livre", dossierId, annee, prefixe],
    `/dossiers/${dossierId}/etats/grand-livre?annee=${annee}${prefixe ? `&prefixe=${prefixe}` : ""}`,
    GrandLivreSchema,
  );

  const filtre = (
    <label className="filtre">
      Filtrer par préfixe de compte
      <input
        value={prefixe}
        onChange={(e) => setPrefixe(e.target.value.replace(/\D/g, ""))}
        placeholder="601, 41, 5…"
        inputMode="numeric"
      />
    </label>
  );

  if (q.isLoading) return <>{filtre}<EtatChargement quoi="du grand livre" /></>;
  if (q.error) return <EtatErreur erreur={q.error} />;
  if (!q.data?.valeur)
    return <>{filtre}<Refus avis={q.data?.avis ?? []} quoi="Le grand livre" /></>;

  const gl = q.data.valeur;
  const compte = gl.comptes.find((c) => c.numero === ouvert);

  return (
    <div>
      {filtre}

      <p className="resume">
        Les sous-comptes restent listés tels quels : <strong>on ne regroupe jamais
        des sous-comptes</strong>. Le total de la branche est donné à part.
      </p>

      <section>
        <h2>Comptes mouvementés</h2>
        <Tableau
          colonnes={[
            { cle: "n", titre: "N°", rendu: (c) => <code>{c.numero}</code> },
            { cle: "int", titre: "Intitulé", rendu: (c) => c.intitule },
            { cle: "an", titre: "À-nouveau", montant: true, rendu: (c) => fcfa(c.aNouveau) },
            { cle: "d", titre: "Débit", montant: true, rendu: (c) => fcfa(c.totalDebit) },
            { cle: "c", titre: "Crédit", montant: true, rendu: (c) => fcfa(c.totalCredit) },
            {
              cle: "s",
              titre: "Solde",
              montant: true,
              rendu: (c) => `${fcfa(Math.abs(c.soldeFinal))} ${c.sensSoldeFinal}`,
            },
          ]}
          lignes={gl.comptes}
          cle={(c) => c.numero}
          surLigne={(c) => setOuvert(ouvert === c.numero ? null : c.numero)}
        />
      </section>

      {compte && (
        <section>
          <h2>
            {compte.numero} — {compte.intitule}
          </h2>
          <Tableau
            colonnes={[
              { cle: "date", titre: "Date", rendu: (m) => m.date },
              { cle: "jrn", titre: "Journal", rendu: (m) => m.journal },
              { cle: "ref", titre: "Pièce", rendu: (m) => <code>{m.reference}</code> },
              { cle: "lib", titre: "Libellé", rendu: (m) => m.libelle },
              { cle: "d", titre: "Débit", montant: true, rendu: (m) => (m.debit ? fcfa(m.debit) : "") },
              { cle: "c", titre: "Crédit", montant: true, rendu: (m) => (m.credit ? fcfa(m.credit) : "") },
              {
                cle: "s",
                titre: "Solde",
                montant: true,
                // Le sens est PORTÉ, jamais déduit à l'affichage.
                rendu: (m) => `${fcfa(m.soldeAbsolu)} ${m.sensSolde}`,
              },
            ]}
            lignes={compte.mouvements}
            cle={(m, i) => `${m.reference}-${i}`}
          />
        </section>
      )}

      <section>
        <h2>Totaux par branche</h2>
        <p className="resume">
          Ce sont des <strong>totaux</strong>, pas un regroupement : les comptes
          restent listés individuellement au-dessus.
        </p>
        <Tableau
          colonnes={[
            { cle: "p", titre: "Racine", rendu: (b) => <code>{b.prefixe}</code> },
            { cle: "i", titre: "Intitulé", rendu: (b) => b.intitule },
            { cle: "n", titre: "Comptes", montant: true, rendu: (b) => b.nbComptes },
            { cle: "s", titre: "Solde net", montant: true, rendu: (b) => fcfa(b.soldeNet) },
          ]}
          lignes={gl.branches}
          cle={(b) => b.prefixe}
        />
      </section>

      <ListeAvis avis={q.data.avis} titre="Observations" />
    </div>
  );
}

// ===========================================================================
// COMPTE DE RÉSULTAT
// ===========================================================================

const CrSchema = z.object({
  sig: z.array(z.object({ ref: z.string(), libelle: z.string(), montant: z.number() })),
  xv: z.object({ ref: z.string(), libelle: z.string(), montant: z.number() }),
  produits: z.number(),
  charges: z.number(),
  resultatNet: z.number(),
  soldeClasses678: z.number(),
  controleR2: z.boolean(),
  estPubliable: z.boolean(),
});

export function CompteResultat({ dossierId, annee }: EcranProps) {
  const q = useApiEnveloppe(
    ["compte-resultat", dossierId, annee],
    `/dossiers/${dossierId}/etats/compte-resultat?annee=${annee}`,
    CrSchema,
  );

  if (q.isLoading) return <EtatChargement quoi="du compte de résultat" />;
  if (q.error) return <EtatErreur erreur={q.error} />;
  if (!q.data?.valeur) return <Refus avis={q.data?.avis ?? []} quoi="Le compte de résultat" />;

  const cr = q.data.valeur;

  return (
    <div>
      <Tuiles
        tuiles={[
          { titre: "Produits", valeur: fcfa(cr.produits) },
          { titre: "Charges", valeur: fcfa(cr.charges) },
          { titre: "Résultat net", valeur: fcfa(cr.resultatNet) },
          {
            titre: "Contrôle R2",
            valeur: cr.controleR2 ? "passé" : "échoué",
            note: "XI = solde des classes 6/7/8",
            alerte: !cr.controleR2,
          },
        ]}
      />

      <section>
        <h2>Soldes intermédiaires de gestion</h2>
        <Tableau
          colonnes={[
            { cle: "ref", titre: "Réf.", rendu: (s) => <code>{s.ref}</code> },
            { cle: "lib", titre: "Libellé", rendu: (s) => s.libelle },
            { cle: "m", titre: "Montant", montant: true, rendu: (s) => fcfa(s.montant) },
          ]}
          lignes={cr.sig}
          cle={(s) => s.ref}
          total={(s) => s.ref === "XI"}
        />
      </section>

      <section>
        <h2>Charges et produits non ventilés</h2>
        <p className="resume">
          Le poste <code>XV</code> garantit que le résultat net égale le solde des
          classes 6/7/8 <strong>au lieu de laisser l'écart se dissoudre</strong>.
        </p>
        <p className={cr.xv.montant === 0 ? "oui" : "non"}>
          {cr.xv.libelle} : <strong>{fcfa(cr.xv.montant)}</strong>
        </p>
      </section>

      <ListeAvis avis={q.data.avis} titre="Contrôles et observations" />
    </div>
  );
}

// ===========================================================================
// BILAN
// ===========================================================================

const PosteSchema = z.object({ ref: z.string(), libelle: z.string(), montant: z.number() });

const BilanSchema = z.object({
  actif: z.array(PosteSchema),
  passif: z.array(PosteSchema),
  nonMappes: z.array(z.string()),
  totalActif: z.number(),
  totalPassif: z.number(),
  resultat: z.number(),
  b1EquilibreActifPassif: z.boolean(),
  b4TousComptesRattaches: z.boolean(),
  estPubliable: z.boolean(),
});

export function Bilan({ dossierId, annee }: EcranProps) {
  const q = useApiEnveloppe(
    ["bilan", dossierId, annee],
    `/dossiers/${dossierId}/etats/bilan?annee=${annee}`,
    BilanSchema,
  );

  if (q.isLoading) return <EtatChargement quoi="du bilan" />;
  if (q.error) return <EtatErreur erreur={q.error} />;
  if (!q.data?.valeur) return <Refus avis={q.data?.avis ?? []} quoi="Le bilan" />;

  const b = q.data.valeur;

  const colonnes = [
    { cle: "ref", titre: "Réf.", rendu: (p: z.infer<typeof PosteSchema>) => <code>{p.ref}</code> },
    { cle: "lib", titre: "Poste", rendu: (p: z.infer<typeof PosteSchema>) => p.libelle },
    { cle: "m", titre: "Montant", montant: true, rendu: (p: z.infer<typeof PosteSchema>) => fcfa(p.montant) },
  ];

  return (
    <div>
      <section className="controles">
        <h2>Contrôles</h2>
        <ul>
          <li className={b.b1EquilibreActifPassif ? "oui" : "non"}>
            B1 — Actif = Passif : {b.b1EquilibreActifPassif ? "passé" : "échoué"}
          </li>
          <li className={b.b4TousComptesRattaches ? "oui" : "non"}>
            B4 — tous les comptes rattachés :{" "}
            {b.b4TousComptesRattaches ? "passé" : `${b.nonMappes.length} compte(s) non prévus`}
          </li>
        </ul>
      </section>

      {b.nonMappes.length > 0 && (
        <section>
          <h2>Comptes hors mapping</h2>
          <p className="resume">
            Un compte hors mapping <strong>ne se perd pas</strong> : il tombe dans
            un poste de recueil et il est listé ici. Sans cette règle, le bilan
            cessait de boucler sans explication.
          </p>
          <ul className="non-mappes">
            {b.nonMappes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </section>
      )}

      <div className="bilan-deux-colonnes">
        <section>
          <h2>Actif — {fcfa(b.totalActif)}</h2>
          <Tableau colonnes={colonnes} lignes={b.actif} cle={(p) => p.ref} />
        </section>

        <section>
          <h2>Passif — {fcfa(b.totalPassif)}</h2>
          <Tableau colonnes={colonnes} lignes={b.passif} cle={(p) => p.ref} />
        </section>
      </div>

      <ListeAvis avis={q.data.avis} titre="Contrôles et observations" />
    </div>
  );
}
